<script setup lang="ts">
import {useReader} from "~/composables/useReader";
import ProjectComment from "~/components/ProjectComment.vue";
import ProjectCard from "~/components/ProjectCard.vue";
import {formatSeniority} from "~/utils/format";
import type {ApiError} from "~/types/ApiError";

const route = useRoute();
const router = useRouter();
const appUser = useAppUser();
const readerUuid = route.params.reader;

if (typeof readerUuid !== "string") {
  throw createError({
    statusCode: 400,
    statusMessage: "UUID de lecteur invalide",
    fatal: true,
  });
}

const {getReader, getBookmarks, getComments} = useReader();

const bookmarksPage = ref(1);
const commentsPage = ref(1);

// Fetch data
const [
  {data: readerResponse, status: readerStatus, error: readerError},
  {data: bookmarksResponse, status: bookmarksStatus},
  {data: commentsResponse, status: commentsStatus},
] = await Promise.all([
  getReader(readerUuid),
  getBookmarks(readerUuid, bookmarksPage),
  getComments(readerUuid, commentsPage),
]);

if (readerError.value) {
  const errorData = readerError.value.data as ApiError;
  throw createError({
    statusCode: readerError.value.statusCode || 500,
    statusMessage: errorData?.message || "Lecteur non trouvé",
    fatal: true,
  });
}

const reader = computed(() => readerResponse.value?.data);

useWreadiaSeo({
  title: () => reader.value?.name,
  description: () => reader.value?.profiles?.reader?.bio,
  image: () => reader.value?.profiles?.reader?.picture,
  ogType: "profile",
  extra: {
    profileUsername: () => reader.value?.name,
  }
});

if (!reader.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Lecteur non trouvé",
    fatal: true,
  });
}

const bookmarks = computed(() => bookmarksResponse.value?.data ?? []);
const bookmarksMeta = computed(() => bookmarksResponse.value?.meta ?? null);

const comments = computed(() => commentsResponse.value?.data ?? []);
const commentsMeta = computed(() => commentsResponse.value?.meta ?? null);

const hasBookmarksPagination = computed(() => !!bookmarksMeta.value &&
    Number(bookmarksMeta.value.total) > Number(bookmarksMeta.value.per_page) &&
    Number(bookmarksMeta.value.per_page) > 0);

const hasCommentsPagination = computed(() => !!commentsMeta.value &&
    Number(commentsMeta.value.total) > Number(commentsMeta.value.per_page) &&
    Number(commentsMeta.value.per_page) > 0);

const tabs = [
  {
    label: "Histoires lues",
    value: "bookmarks",
    slot: "bookmarks",
  },
  {
    label: "Commentaires",
    value: "comments",
    slot: "comments",
  },
];

const selectedTab = computed({
  get() {
    return (route.query.tab as string) || (tabs[0]?.value as string);
  },
  set(value) {
    router.replace({
      query: {
        ...route.query,
        tab: value,
      },
    });
  },
});
</script>

<template>
  <UPage id="pagePage">
    <UPageBody id="pageBody">
      <UContainer>
        <div v-if="readerStatus === 'pending'" class="flex justify-center py-20">
          <UIcon name="i-heroicons-arrow-path" class="animate-spin h-12 w-12"/>
        </div>

        <div v-else-if="reader">
          <div class="flex flex-col lg:flex-row gap-16">
            <div class="lg:flex-4/12 lg:pr-16 lg:border-r-2 border-gray-100">
              <div class="flex justify-between items-start">
                <UAvatar
                    :src="reader.profiles?.reader?.picture"
                    :alt="reader.name"
                    class="mb-6 w-20 h-20"
                />
                <UButton v-if="appUser && appUser.uuid === reader.uuid" color="secondary" variant="solid" size="sm" to="/dashboard/profile">Modifier
                  mon profil
                </UButton>
              </div>

              <h1 class="text-2xl mb-1">
                {{ reader.name }}
              </h1>

              <p class="text-sm text-gray-300 mb-4">
                Lecteur•rice depuis {{ formatSeniority(reader.created_at) }}
              </p>

              <div v-if="reader.profiles?.reader?.bio" class="whitespace-pre-wrap mb-6" v-html="nl2br(reader.profiles.reader.bio)"></div>

              <USeparator class="my-6"></USeparator>

              <div class="flex lg:flex-col justify-start gap-x-4 space-y-2">
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-book-pile" class="size-5"/>
                    <strong>{{ reader.read_projects_count || 0 }}</strong>
                  </span>
                  <span>
                    {{ pluralize(reader.read_projects_count || 0, "histoire lue", "histoires lues") }}
                  </span>
                </div>
                <div class="flex flex-col md:flex-row gap-x-1">
                  <span class="flex flex-nowrap gap-x-2 items-center">
                    <UIcon name="i-custom-comment" class="size-5"/>
                    <strong>{{ reader.comments_count || 0 }}</strong>
                  </span>
                  <span>
                    {{ pluralize(reader.comments_count || 0, "commentaire laissé", "commentaires laissés") }}
                  </span>
                </div>
              </div>
            </div>

            <div class="lg:flex-8/12">
              <UTabs v-model="selectedTab" :items="tabs" class="w-full" variant="link" :ui="{root:'gap-12'}">
                <!-- Onglet Histoires lues -->
                <template #bookmarks>
                  <div v-if="bookmarksStatus === 'pending' && !bookmarks.length" class="flex justify-center">
                    <UIcon name="i-heroicons-arrow-path" class="animate-spin"/>
                  </div>
                  <div v-else-if="bookmarks.length > 0">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ProjectCard
                          v-for="bookmark in bookmarks"
                          :key="bookmark.project.uuid"
                          :project="bookmark.project"
                          responsive-layout="detail"
                      />
                    </div>

                    <div v-if="bookmarksMeta && hasBookmarksPagination" class="flex justify-center">
                      <UPagination
                          v-model:page="bookmarksPage"
                          :total="bookmarksMeta.total"
                          :items-per-page="bookmarksMeta.per_page"
                      />
                    </div>
                  </div>
                  <p v-else-if="bookmarksStatus !== 'pending'">
                    Aucune histoire lue pour le moment.
                  </p>
                </template>

                <!-- Onglet Commentaires -->
                <template #comments>
                  <div v-if="commentsStatus === 'pending' && !comments.length" class="flex justify-center">
                    <UIcon name="i-heroicons-arrow-path" class="animate-spin"/>
                  </div>
                  <div v-else-if="comments.length > 0" class="flex flex-col">
                    <ProjectComment
                        v-for="comment in comments"
                        :key="comment.id"
                        :comment="comment"
                        show-context
                    />

                    <div v-if="commentsMeta && hasCommentsPagination" class="flex justify-center">
                      <UPagination
                          v-model:page="commentsPage"
                          :total="commentsMeta.total"
                          :items-per-page="commentsMeta.per_page"
                      />
                    </div>
                  </div>
                  <p v-else-if="commentsStatus !== 'pending'">
                    Aucun commentaire public pour le moment.
                  </p>
                </template>
              </UTabs>
            </div>
          </div>
        </div>
      </UContainer>
    </UPageBody>
  </UPage>
</template>
